import React, {
  useState,
  useEffect,
  useCallback,
} from 'react';
import { ThemeType } from '@reverbdotcom/cadence/components/types';
import { getItem, setItem } from '../local_storage_utils';
import { WEB2_THEME_SWITCH_TOGGLE_EVENT } from './web2_theme_switch';
import { useExpEnabled } from '../user_hooks';
import experiments from '../experiments';

export interface ThemeContextType {
  theme: ThemeType;
  updateTheme: (newTheme: ThemeType) => void;
}

export const ThemeContext = React.createContext<ThemeContextType>({
  theme: ThemeType.default,
  updateTheme: () => {},
});

export function ThemeContextProvider({ children }) {
  const contextValue = useThemeContextProvider();

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return React.useContext(ThemeContext);
}

function useThemeContextProvider(): ThemeContextType {
  const initialTheme = useExpEnabled(experiments.WEB2_LOOK_AND_FEEL_2) ? ThemeType.web2 : ThemeType.default;
  const [theme, setTheme] = useState<ThemeType>(initialTheme);

  const isInThemeSwitchExperiment = useExpEnabled(experiments.REVERB_2_THEME);

  useEffect(() => {
    if (!isInThemeSwitchExperiment) return;

    const localStorageKey = getItem('reverb-theme') as ThemeType;

    if (localStorageKey) {
      updateTheme(localStorageKey);
    }
  }, []);

  const updateTheme = useCallback((newTheme: ThemeType) => {
    setTheme(newTheme);
    setElementAttribute(newTheme);
    setItem('reverb-theme', newTheme);
  }, [setTheme]);

  useEffect(() => {
    function handleEvent(e) {
      const newTheme = e?.detail?.newTheme;

      if (newTheme) {
        updateTheme(newTheme);
      }
    }

    window.addEventListener(WEB2_THEME_SWITCH_TOGGLE_EVENT, handleEvent);
    return () => {
      window.removeEventListener(WEB2_THEME_SWITCH_TOGGLE_EVENT, handleEvent);
    };
  }, [updateTheme]);

  return { theme, updateTheme };
}

function setElementAttribute(themeName) {
  // eslint-disable-next-line prefer-destructuring
  const htmlTag = document.getElementsByTagName('html')?.[0];
  if (!htmlTag) return;

  htmlTag.setAttribute('data-theme', themeName);
}
